import React, {
  memo, useContext, useCallback, useEffect, useRef,
} from 'react'

import { Spinner, Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import Modal from 'Pods/Shared/Modal/Modal'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { closeCommentsModal, openSignInModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import CommentThread from './Components/CommentThread/CommentThread'
import Composer from './Components/Composer/Composer'
import useModelComments from './Hooks/useModelComments'

function CommentsModal() {
  const t = useTranslation()
  const { locale, userLoggedIn } = useContext(ThreeDStudioPageContext)
  const pageConfig = usePageConfig()

  const isOpen = useThreeDStudioStore(state => state.ui.isCommentsModalOpen)
  const slug = useThreeDStudioStore(state => state.ui?.openedModel?.slug)
  const title = useThreeDStudioStore(
    state => state.ui?.openedModel?.title ?? state.ui?.openedModel?.prompt,
  )

  const currentUserId = pageConfig?.currentUserId ?? null
  const canComment = !!userLoggedIn

  const {
    comments,
    totalCommentsCount,
    hasMore,
    loadMore,
    isLoading,
    loadFailed,
    submitFailed,
    limitReached,
    isSubmitting,
    addComment,
    addReply,
    deleteComment,
  } = useModelComments({ slug, isOpen })

  const countRef = useRef(null)
  const sentinelRef = useRef(null)

  // Infinite scroll over the paginated root comments: fetch the next page when
  // the sentinel under the list nears the modal body's scroll fold. The root is
  // the shared Modal's scroll container - against the viewport the overflow clip
  // would cancel the rootMargin prefetch. loadMore guards itself against
  // re-entry (in-flight fetch, exhausted list, failed load), so a re-observe
  // after an append is a cheap no-op when nothing should happen.
  useEffect(() => {
    const sentinel = sentinelRef.current

    if (!sentinel || typeof IntersectionObserver === 'undefined') {
      return undefined
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMore()
        }
      },
      { root: sentinel.closest('.app-modal__body'), rootMargin: '200px', threshold: 0 },
    )

    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [hasMore, loadMore])

  const handleLogin = useCallback(() => {
    closeCommentsModal()
    // The prompt's button reads "Log in", so open on the login screen rather
    // than the store's default sign-up screen.
    openSignInModal('login')
  }, [])

  // The Delete button unmounts with the comment it belongs to, which drops focus
  // to <body> - outside the dialog, so Escape stops closing it and Tab restarts
  // from the top of the page. Focus moves to the comments count instead, which
  // outlives any single comment. Deferred by a frame because react-modal restores
  // focus to the confirm modal's trigger as it unmounts, undoing an earlier call.
  const handleDelete = useCallback((commentId) => {
    deleteComment(commentId)
    requestAnimationFrame(() => countRef.current?.focus())
  }, [deleteComment])

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeCommentsModal}
      width="small"
      header={title}
      customClass="comments-modal"
      ariaLabel={t('commentsModal.ariaLabel')}
    >
      {canComment ? (
        <Composer
          placeholder={t('commentsModal.placeholder')}
          username={pageConfig?.currentUserUsername || ''}
          avatarUrl={pageConfig?.currentUserAvatarUrl || null}
          isSubmitting={isSubmitting}
          onSubmit={addComment}
        />
      ) : (
        <div className="comments-modal__login-prompt">
          <span className="cgt-body-base">{t('commentsModal.loginPrompt')}</span>
          <Button type="secondary" size="small" onClick={handleLogin}>
            {t('commentsModal.login')}
          </Button>
        </div>
      )}

      {(submitFailed || limitReached) && (
        <div className="comments-modal__error" role="alert">
          {t(limitReached ? 'commentsModal.limitReached' : 'commentsModal.submitError')}
        </div>
      )}

      <div className="comments-modal__divider" />

      <div className="comments-modal__count cgt-body-base-semibold" ref={countRef} tabIndex={-1}>
        {t('commentsModal.commentsCount', { count: totalCommentsCount ?? comments.length })}
      </div>

      {isLoading && comments.length === 0 && (
        <div className="comments-modal__state" aria-live="polite">
          <Spinner size="small" />
        </div>
      )}

      {!isLoading && loadFailed && comments.length === 0 && (
        <div className="comments-modal__state" role="alert">
          {t('commentsModal.loadError')}
        </div>
      )}

      {!isLoading && !loadFailed && comments.length === 0 && (
        <div className="comments-modal__state" role="status">
          {t('commentsModal.empty')}
        </div>
      )}

      {/* Kept mounted while a post-mutation refetch runs so replying/adding does
          not blank the tree into a spinner (which reset each thread's expanded
          replies and open reply form). */}
      {comments.length > 0 && (
        <>
          <ul className="comments-modal__list">
            {comments.map(comment => (
              <CommentThread
                key={comment.id}
                comment={comment}
                currentUserId={currentUserId}
                locale={locale}
                canComment={canComment}
                isSubmitting={isSubmitting}
                onReply={addReply}
                onDelete={handleDelete}
              />
            ))}
          </ul>

          {isLoading && hasMore && (
            <div className="comments-modal__state" aria-live="polite">
              <Spinner size="small" />
            </div>
          )}

          {loadFailed && (
            <div className="comments-modal__error" role="alert">
              {t('commentsModal.loadError')}
            </div>
          )}

          {hasMore && <div className="comments-modal__sentinel" ref={sentinelRef} aria-hidden="true" />}
        </>
      )}
    </Modal>
  )
}

export default memo(CommentsModal)
